import React, { Component } from 'react';
import { connect } from 'react-redux';
import { pick, values, omit, times } from 'lodash';
import { SubmissionError } from 'redux-form';
import { injectIntl, defineMessages } from 'react-intl';

import { getURLParams } from '../../lib/functions';
import buildEventDispatcher from '../../lib/event-dispatcher';
import webhookDispatcher from '../../lib/webhook-dispatcher';
import Loading from '../../../components/loading';
import PurchaseClaimComponent, { FORM_NAME, formSelector } from '../../components/purchase-claim/index';
import { formatContentWithEmoji } from '../../components/alert';
import Purchase from '../../lib/purchase';
import {
  getPurchaseConsumerFieldSchemas,
  getMemberConsumerFieldSchemas,
} from '../../selectors/purchase-selector';
import {
  EVEN_SPLIT,
  FIXED_PER_PERSON,
} from '../../lib/split-types';
import events, { EVENT_CLAIM_LOADED, EVENT_PURCHASE_CLAIM_CLOSED } from '../../lib/events';
import { formatMessage } from '../../lib/intl';
import { reportError } from '../../lib/error-reporter';
import {
  submitClaimForm,
  hideDuplicatedPurchaseModal,
  showDuplicatedPurchaseModal,
  showModalInnerAlert,
  showModalTopAlert,
  hideModalTopAlert,
  updateUserLanguage,
} from '../../actions';

const COMPONENT_ID = 'DesktopPurchaseClaimContainer';

const i18n = defineMessages({
  freePurchaseAlertTitle: {
    id: 'claim.freePurchaseAlert.title',
    defaultMessage: 'Want to make this purchase free for you?',
  },
  freePurchaseAlertExplainer: {
    id: 'claim.freePurchaseAlert.explainer',
    defaultMessage: 'Only include who will be paying in the share count',
  },
});

class PurchaseClaimContainer extends Component {
  formatMessage = formatMessage(this.props.intl ? this.props.intl : {});

  constructor(props) {
    super(props);

    const { purchase } = props;

    const redirectToDashboard = !Purchase.isCreated(purchase.status);
    const successReturnUrl = Purchase.getSuccessReturnUrlWithParams(purchase);

    let redirectTo = false;
    let breakOutIframe = false;

    if (redirectToDashboard) {
      redirectTo = successReturnUrl ?  successReturnUrl : `/purchases/${purchase.slug}`;

      if (successReturnUrl) {
        breakOutIframe = true;
      }
    }

    window.addEventListener('message', (event) => {
      if (event.data.eventName && event.data.eventName === EVENT_PURCHASE_CLAIM_CLOSED) {
        this.props.eventDispatcher.dispatchPurchaseClaimClosed(FORM_NAME);
      }
    }, false);

    this.onSubmit = this.onSubmit.bind(this);
    this.updateUserLanguage = this.updateUserLanguage.bind(this);
    this.state = {
      redirectTo,
      breakOutIframe,
    };
  }

  componentDidMount() {
    const { purchase, authentication, renderedLanguage } = this.props;
    const wantedData = ['product', 'status', 'group'];
    if (purchase.allowOrganizerForFree) {
      this.showFreePurchaseAlert();
    }
    const data = {
      ...pick(purchase, wantedData),
      renderedLanguage,
      browserLanguage: navigator.language,
    };
    events.publish(EVENT_CLAIM_LOADED, {
      entityId: purchase.id,
      entityType: 'Purchase',
    },
      data,
      authentication,
    );
  }

  componentWillReceiveProps(nextProps) {
    if (!this.props.duplicatedPurchase && nextProps.duplicatedPurchase) {
      this.showDuplicatedPurchaseWarnings(nextProps.duplicatedPurchase);
    }
    if (this.props.duplicatedPurchase && !nextProps.duplicatedPurchase) {
      this.props.hideModalTopAlert();
    }
    if (this.props.purchase.status !== nextProps.purchase.status) {
      webhookDispatcher.dispatchPurchaseClaimed(this.props.purchase)
        .then(() => this.setState({ redirectTo: this.afterSubmitRedirectUrl() }));
    }
  }

  updateUserLanguage(language, token) {
    this.props.eventDispatcher.dispatchLanguageChangedEvent(language);
    this.props.updateUserLanguage(language, token);
  }

  onSubmit(data) {
    const { purchase, authentication } = this.props;
    return this.props.submitClaimForm(purchase, data, authentication)
      .catch((error) => {
        reportError({
          error,
          component: COMPONENT_ID,
          params: {
            purchaseId: purchase.id,
            ...omit(data, ['auth', 'organizerPassword']),
            authentication,
          }
        });

        if (error.is401) {
          throw new SubmissionError({
            organizerPassword: 'invalid password',
          });
        }

        const content = formatContentWithEmoji(error.message, '‼️');
        this.props.showModalInnerAlert(content);
      });
  }

  afterSubmitRedirectUrl() {
    const { purchase, hasPurchaseConsumerFieldSchemas } = this.props;

    if (hasPurchaseConsumerFieldSchemas) {
      return `/purchases/${purchase.slug}/purchase-fields`;
    }

    return `/purchases/${purchase.slug}/organizer-commit`;
  }

  showDuplicatedPurchaseWarnings(duplicatedPurchase) {
    const text = (
      <span>
        <span className="mr-4">
          <b>Warning!</b> You own a similar group.
        </span>
        <a href={`/purchases/${duplicatedPurchase.slug}`} target="_top">Go to existing group</a>
      </span>
    );

    if (this.props.authentication.token) {
      this.props.showDuplicatedPurchaseModal();
    } else {
      const content = formatContentWithEmoji(text, '‼️');
      this.props.showModalTopAlert(content, 'warning');
    }
  }

  showFreePurchaseAlert() {
    const text = (
      <span>
        <span className="mr-4">
          <b>{this.formatMessage(i18n.freePurchaseAlertTitle)}</b>
          {' '}
          {this.formatMessage(i18n.freePurchaseAlertExplainer)}.
        </span>
      </span>
    );
    const content = formatContentWithEmoji(text, '☝️');
    this.props.showModalTopAlert(content, 'success');
  }

  render() {
    const { purchase } = this.props;

    if (this.state.redirectTo) {
      if (this.state.breakOutIframe) {
        window.top.location = this.state.redirectTo;
      } else {
        window.location = this.state.redirectTo;
      }
      return <Loading />;
    }

    const costType = purchase.product.costType;
    const defaultSplitType = Purchase.isPerSlot(costType) ? FIXED_PER_PERSON : EVEN_SPLIT;
    const minSlots = purchase.group.minSlots || (purchase.product.minSlots === 1 ? 2 : purchase.product.minSlots);

    const qsFormValues = getURLParams();

    const inviteeDefaults = {
      allowedShareAmount: {
        amountCents: 0,
        currencyCode: purchase.currencyCode,
      },
    };

    let invitees;

    if (qsFormValues.invitees) {
      invitees = qsFormValues.invitees.map((invitee) => {
        return { ...inviteeDefaults, ...invitee };
      });

      if (invitees.length > 1) {
        invitees = [...invitees, inviteeDefaults]
      } else {
        invitees = [...invitees, ...times(2, () => {
          return inviteeDefaults;
        })];
      }
    } else {
      invitees = times(2, () => {
        return inviteeDefaults;
      });
    }

    const initialValues = {
      splitType: qsFormValues.splitType || defaultSplitType,
      organizerEmail: purchase.group.organizerEmail,
      organizerName: purchase.group.organizerFullName,
      minSlots: parseInt(qsFormValues.minSlots, 10) || minSlots,
      invitees,
      personalMessage: (qsFormValues.message || ''),
      minContribution: qsFormValues.minContribution || null,
    };

    const props = {
      ...pick(this.props, [
        'invitees',
        'splitType',
        'purchase',
        'duplicatedPurchase',
        'legalDocuments',
        'isDuplicatedPurchaseModalVisible',
        'hideDuplicatedPurchaseModal',
        'accessToken',
        'authenticationMethod',
      ]),
      initialValues,
      updateUserLanguage: this.updateUserLanguage,
      onSubmit: this.onSubmit,
    };

    return (
      <PurchaseClaimComponent {...props} />
    );
  }
}

const mapStateToProps = (state) => {
  const { session: { authentication }, purchases, session, legalDocuments, ui } = state;
  const duplicatedPurchase = purchases.byId[purchases.duplicated];
  const purchase = purchases.byId[purchases.current];
  const hasPurchaseConsumerFieldSchemas = getPurchaseConsumerFieldSchemas(state).length > 0;
  const hasMemberConsumerFieldSchemas = getMemberConsumerFieldSchemas(state).length > 0;

  if (!purchase) return {};

  return {
    invitees: formSelector(state, 'invitees'),
    splitType: formSelector(state, 'splitType'),
    eventDispatcher: buildEventDispatcher(state),
    purchase,
    duplicatedPurchase,
    renderedLanguage: session.language,
    authentication: session.authentication,
    legalDocuments: values(legalDocuments.byId) || [],
    isDuplicatedPurchaseModalVisible: duplicatedPurchase && ui.modals.duplicatedPurchase,
    hasPurchaseConsumerFieldSchemas,
    hasMemberConsumerFieldSchemas,
    accessToken: authentication.token,
    authenticationMethod: state.users.existence,
  };
};

const mapDispatchToProps = {
  submitClaimForm,
  showDuplicatedPurchaseModal,
  hideDuplicatedPurchaseModal,
  showModalInnerAlert,
  showModalTopAlert,
  hideModalTopAlert,
  updateUserLanguage,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(PurchaseClaimContainer));



// WEBPACK FOOTER //
// ./src/desktop/containers/purchase-claim/index.container.js